// Content for the Imprint MIS case study (Boost! Design & Marketing portfolio piece)

const A = 'https://customer-assets-gfyr7b9c.emergentagent.net/job_brand-ascent-1/artifacts';

export const CS_IMAGES = {
  hero: `${A}/9xcxr1qv_Main.jpeg`,
  admin: `${A}/h09xvisi_Admin.jpeg`,
  crm: `${A}/hl8m8us4_Admin%202.jpeg`,
  analytics: `${A}/emrq8c45_Analytics.jpeg`,
  integrations: `${A}/4jxft0xz_Integ.jpeg`,
};

export const BOOST_LOGO =
  'https://customer-assets-lqy194kg.emergentagent.net/job_31bcde7c-d98a-4c27-a3ab-d96f08806001/artifacts/e3mvklhv_Logo%20Pink.png';

export const CS_HERO = {
  eyebrow: 'Boost! · Case Study',
  chips: ['Web Design', 'Full-Stack Build', 'SEO / GEO', 'Branding'],
  year: '2025–2026',
  title: 'Reimagining the future of intelligent printing',
  sub:
    'A ground-up website and custom platform rebuild for Imprint MIS — faster, smarter, and built to be found by both people and AI.',
  client: 'Imprint Business Systems Ltd',
  liveUrl: 'https://imprint-mis.co.uk',
};

export const CS_CLIENT = {
  kicker: '01 — The Client',
  title: 'A quiet technical leader that deserved a louder platform',
  copy: [
    'Imprint MIS provides bespoke Management Information Systems and 24+ pre-built integrations for the print & packaging industry — JDF workflow, real-time machine data and CIP4 among them.',
    'Established in 1979 and members of the IPIA and Picon, Imprint are genuinely a technical leader. Their old website was dated, slow and invisible to search — it undersold everything they had built.',
  ],
  facts: [
    { value: 'Est. 1979', label: 'In business 45+ years' },
    { value: 'IPIA · Picon', label: 'Industry members' },
    { value: '24+', label: 'Pre-built integrations' },
    { value: 'JDF · CIP4', label: 'Workflow specialists' },
  ],
};

export const CS_CHALLENGE = {
  kicker: '02 — The Challenge',
  title: 'A modern, high-tech company hidden behind a dated website',
  items: [
    {
      icon: 'Gauge',
      title: 'Slow & dated',
      copy: 'An outdated, sluggish site that did nothing to reflect a modern, high-tech company.',
    },
    {
      icon: 'SearchX',
      title: 'Invisible to search',
      copy: 'Poor search visibility, with legacy URLs throwing 404 and noindex errors.',
    },
    {
      icon: 'FilePenLine',
      title: 'No way to publish',
      copy: 'No easy way for the team to publish content, manage documents or grow an audience.',
    },
    {
      icon: 'CloudOff',
      title: 'Content disappearing',
      copy: 'Uploaded assets vanished whenever the hosting container recycled — nothing was safe.',
    },
  ],
};

export const CS_APPROACH = {
  kicker: '03 — Our Approach',
  title: 'Rebuild the brand, the platform and the visibility — together',
  items: [
    {
      icon: 'Palette',
      title: 'Brand-aligned redesign',
      copy: 'Outfit / Inter type, cyan-on-dark, and a distinctive, animated, mobile-first UI.',
    },
    {
      icon: 'Boxes',
      title: 'A custom full-stack platform',
      copy: 'So the client is never dependent on a page-builder — or a developer — to make updates.',
    },
    {
      icon: 'Sparkles',
      title: 'SEO + GEO from day one',
      copy: 'Engineered so the site ranks on Google and gets cited by AI search engines.',
    },
    {
      icon: 'HardDrive',
      title: 'Rock-solid storage',
      copy: 'Persistent GridFS storage so content and imagery survive every redeploy, forever.',
    },
  ],
};

export const CS_SOLUTION = {
  kicker: '04 — The Solution',
  title: 'One platform. Everything the team needs to run and grow.',
  public: {
    label: 'The public site',
    features: [
      {
        icon: 'LayoutTemplate',
        title: 'Modern multi-page marketing site',
        copy: 'Dynamic hero, print-sector pages, an integrations showcase, About, Meet the Team, a Resources hub and a considered Contact experience.',
      },
      {
        icon: 'Smartphone',
        title: 'Fully mobile-responsive',
        copy: 'A React-portaled mobile menu and smooth anchor scrolling — flawless from phone to desktop.',
      },
      {
        icon: 'ShieldCheck',
        title: 'GDPR-compliant by design',
        copy: 'Cookie-consent banner and privacy compliance baked in from the start.',
      },
    ],
  },
  admin: {
    label: 'The custom admin platform',
    sub: 'The part clients love — total control, no developer required.',
    features: [
      {
        icon: 'PenSquare',
        title: 'Headless CMS + rich-text editor',
        copy: 'Write and publish blog posts and case studies with a full rich-text editor.',
      },
      {
        icon: 'Crop',
        title: 'Built-in image cropping',
        copy: 'Enforced hero aspect ratios for perfectly consistent imagery, every time.',
      },
      {
        icon: 'FolderOpen',
        title: 'Document library',
        copy: 'A secure home for whitepapers and downloadable assets.',
      },
      {
        icon: 'Users',
        title: 'CRM + newsletter audience',
        copy: 'Contact-form opt-ins, CSV import with smart column auto-detection, a duplicate-check preview and GDPR-safe handling of past unsubscribes — with source tracking throughout.',
      },
      {
        icon: 'Send',
        title: 'One-click “Send to Subscribers”',
        copy: 'Emails a beautifully branded article newsletter to the whole list via Resend, with secure one-click unsubscribe and an automatic welcome email for new sign-ups.',
      },
      {
        icon: 'CreditCard',
        title: 'Business card generator',
        copy: 'On-brand business cards for the whole team, generated in seconds.',
      },
      {
        icon: 'BarChart3',
        title: 'Analytics dashboard',
        copy: 'A lightweight built-in page-view tracker plus a live Google Search Console integration (OAuth) — real search performance, inside their own admin.',
      },
    ],
  },
  shots: [
    { src: CS_IMAGES.admin, url: 'imprint-mis.co.uk/admin', caption: 'Admin dashboard — content, documents & settings in one place.' },
    { src: CS_IMAGES.crm, url: 'imprint-mis.co.uk/admin/crm', caption: 'Newsletter CRM — CSV import with smart auto-detection.' },
    { src: CS_IMAGES.analytics, url: 'imprint-mis.co.uk/admin/analytics', caption: 'Site analytics — first-party tracking + Google Search Console.' },
    { src: CS_IMAGES.integrations, url: 'imprint-mis.co.uk/integrations', caption: '24+ integrations, showcased beautifully.' },
  ],
  builtToLast: {
    label: 'Built to last',
    items: [
      {
        icon: 'Database',
        title: 'Persistent by default',
        copy: 'React + FastAPI + MongoDB, with GridFS persistent storage so content & images survive forever.',
      },
      {
        icon: 'Lock',
        title: 'Secure & audited',
        copy: 'Cloudflare Turnstile anti-spam, JWT-secured admin with brute-force lockout, and a full security audit.',
      },
      {
        icon: 'Mail',
        title: 'Reliable email',
        copy: 'Transactional and newsletter email delivered via Resend.',
      },
    ],
  },
};

export const CS_RESULTS = {
  kicker: '05 — The Results',
  title: 'Measured live. Engineered for speed and discoverability.',
  headline: [
    { value: '287', unit: 'KB', label: 'JavaScript shipped', note: 'gzipped — ~73% smaller over the wire' },
    { value: '43', unit: 'KB', label: 'Largest hero image', note: 'WebP, from a ~1.2 MB library' },
    { value: '16', unit: '', label: 'AI crawlers welcomed', note: 'built to be cited by ChatGPT, Perplexity, Claude & Google AI' },
    { value: '12+', unit: '', label: 'Structured-data schemas', note: 'machine-readable, top to bottom' },
  ],
  groups: [
    {
      icon: 'Zap',
      title: 'Performance',
      points: [
        'Homepage HTML shell just 5.5 KB, first response ~0.67s.',
        'JavaScript 287 KB gzipped — down from 1.06 MB raw (~73% smaller).',
        'CSS a lean 17.9 KB gzipped.',
        'Largest hero image 43.7 KB (WebP); whole 58-image library ~1.2 MB (avg ~21 KB).',
        'All assets gzip-compressed.',
      ],
    },
    {
      icon: 'Search',
      title: 'SEO',
      points: [
        'Complete meta + Open Graph set, canonical URLs, index/follow.',
        'Dynamic XML sitemap — static pages plus every published post.',
        'Sitemap referenced directly from robots.txt.',
      ],
    },
    {
      icon: 'Bot',
      title: 'GEO — the differentiator',
      points: [
        'robots.txt explicitly welcomes 16 named AI crawlers.',
        'llms.txt structured company index (llmstxt.org standard) for accurate AI answers.',
        '12+ JSON-LD types incl. Organization, ProfessionalService, WebSite+SearchAction, FAQPage, Article, BreadcrumbList and more.',
        '24+ pre-built integrations showcased for both people and machines.',
      ],
    },
  ],
  crawlers: [
    'GPTBot', 'OAI-SearchBot', 'ChatGPT-User', 'PerplexityBot', 'Perplexity-User',
    'ClaudeBot', 'Claude-Web', 'anthropic-ai', 'Google-Extended', 'Bingbot',
    'Applebot', 'Applebot-Extended', 'CCBot', 'FacebookBot', 'Meta-ExternalAgent',
    'Amazonbot',
  ],
};

export const CS_TECH = [
  'React', 'Tailwind', 'FastAPI', 'MongoDB / GridFS', 'Resend',
  'Google Search Console API', 'SEO / GEO', 'JSON-LD', 'Cloudflare Turnstile',
];

export const CS_TESTIMONIAL = {
  // Draft thank-you quote written by Boost! — Pete to review/approve or replace.
  quote:
    "Boost! didn't just rebuild our website — they gave Imprint a platform that finally matches the technology we've been building since 1979. It's fast, it's genuinely ours to run, and for the first time we're being found by the people (and the AI) that matter. A brilliant team who felt like part of ours.",
  name: 'Pete Horwood',
  role: 'Commercial Director, Imprint MIS',
  photo: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_brand-ascent-1/artifacts/9j9fgy2s_Pete.png',
};

export const CS_CTA = {
  title: "Got a brand that's outgrown its website?",
  sub: "Let's build something that works as hard as you do.",
  email: 'team@boostmarketing.agency',
  siteUrl: 'https://boostmarketing.agency/#contact',
};
